import {
  useCallback, useEffect, useRef, useState,
} from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import api from '../../api/endpoints';
import { fileUrl } from '../../api/client';
import PageHeader from '../../components/PageHeader';
import LiveTripMap from '../../components/LiveTripMap';
import StatusTimeline from '../../components/StatusTimeline';
import { startOnlinePayment, waitForPayment } from '../../services/payment';
import { connectRealtime, onRealtime } from '../../services/realtime';
import {
  Badge, Button, Empty, Loading, Row,
} from '../../components/Ui';
import ChargeRows from '../../components/ChargeRows';
import { Reveal } from '../../components/Motion';
import { useToast } from '../../context/ToastContext';
import { colors } from '../../theme';
import { currency, dateTime, titleCase } from '../../utils/format';

const FLOW = ['placed', 'accepted', 'preparing', 'ready', 'out_for_delivery', 'delivered'];

const OrderDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);
  const pollRef = useRef(null);

  const load = useCallback(() => {
    api.orders.detail(id)
      .then((res) => setOrder(res.data))
      .catch(() => setOrder(null))
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => { load(); }, [load]);

  // Live rider position for the tracking map, fed by the delivery trip's room.
  /** Metres away when the rider crossed the arrival radius, else null. */
  const [arrived, setArrived] = useState(null);
  const rideId = order && order.delivery && order.delivery.ride && order.delivery.ride._id;

  useEffect(() => {
    if (!rideId) return undefined;
    connectRealtime();
    const offs = [
      onRealtime('order:delivery', load),
      onRealtime('ride:status', load),
      // The rider has reached the door - say so loudly.
      onRealtime('ride:near_drop', (p) => { setArrived((p && p.metres) || 0); load(); }),
    ];
    return () => { offs.forEach((fn) => fn()); };
  }, [rideId, load]);

  useEffect(() => () => { if (pollRef.current && pollRef.current.cancel) pollRef.current.cancel(); }, []);

  // An active order keeps refreshing so the timeline moves without a reload.
  useEffect(() => {
    if (!order || ['delivered', 'cancelled', 'rejected'].includes(order.status)) return undefined;
    const timer = setInterval(load, 20000);
    return () => clearInterval(timer);
  }, [order, load]);

  /** Reopens Razorpay for an unpaid online order. */
  const payNow = async () => {
    setPaying(true);
    try {
      const { paymentId } = await startOnlinePayment({ refType: 'order', refId: id });
      pollRef.current = waitForPayment(paymentId);
      const result = await pollRef.current;
      if (result.status === 'paid') {
        toast.success('Thank you! Your payment is confirmed.', 'Payment received');
        load();
      } else if (result.status !== 'timeout') {
        toast.error('The payment did not go through. You can try again.', 'Payment not completed');
      }
    } catch (err) {
      toast.error(err.message, 'Payment could not start');
    } finally {
      setPaying(false);
    }
  };

  const cancel = async () => {
    const ok = await toast.confirm({
      title: 'Cancel order',
      message: 'Are you sure you want to cancel this order?',
      confirmLabel: 'Cancel order',
      cancelLabel: 'Keep order',
      danger: true,
    });
    if (!ok) return;
    try {
      await api.orders.cancel(id, { reason: 'Cancelled by customer' });
      load();
    } catch (err) {
      toast.error(err.message, 'Could not cancel');
    }
  };

  if (loading) return <Loading text="Loading order…" />;
  if (!order) {
    return <Empty emoji="🧾" title="Order not found" actionLabel="Go back" onAction={() => navigate(-1)} />;
  }

  /* Countdown to the promise this order was given when it was placed. */
  const minsLeft = order.promisedAt
    ? Math.round((new Date(order.promisedAt) - Date.now()) / 60000)
    : null;
  const etaLate = minsLeft !== null && minsLeft < 0;
  const etaText = minsLeft === null
    ? (order.etaMinutes ? `Arriving in about ${order.etaMinutes} min` : '')
    : etaLate
      ? `Running ${Math.abs(minsLeft)} min late`
      : minsLeft <= 1 ? 'Arriving any minute' : `Arriving in about ${minsLeft} min`;

  const isClosed = ['cancelled', 'rejected'].includes(order.status);
  const canCancel = ['placed', 'accepted'].includes(order.status);

  return (
    <div>
      <div className="flex" style={{ alignItems: 'flex-start', gap: 12 }}>
        <div className="grow">
          <PageHeader title={order.orderNo} subtitle={order.partner && order.partner.businessName} />
        </div>
        {/* Raising a ticket from the order carries the order number with it. */}
        <Link
          to={`/support?refType=order&refId=${order._id}&refNo=${encodeURIComponent(order.orderNo)}`}
          className="btn ghost btn-sm"
        >
          🎧 Help
        </Link>
      </div>

      <div className="split">
        <div>
          <Reveal className="card sh-sm">
            <div className="row-between">
              <Badge value={order.status} />
              <span className="t-tiny">{dateTime(order.createdAt)}</span>
            </div>

            {/* The rider is at the door. */}
            {arrived !== null && order.status !== 'delivered' ? (
              <div
                className="row"
                style={{
                  gap: 12, alignItems: 'center', background: colors.successBg,
                  borderRadius: 12, padding: 12, marginTop: 12,
                }}
              >
                <span style={{ fontSize: 20 }}>🛵</span>
                <div>
                  <div className="t-title" style={{ color: colors.success }}>Your rider has arrived</div>
                  <div className="t-tiny">About {arrived} m away - please come down to collect.</div>
                </div>
              </div>
            ) : null}

            {/*
             * What the customer was promised, counting down. `promisedAt` was
             * frozen when the order was placed, so a later change to the shop's
             * prep time never moves an existing promise.
             */}
            {etaText && !['delivered', 'cancelled', 'rejected'].includes(order.status) ? (
              <div
                className="row"
                style={{
                  gap: 12, alignItems: 'center',
                  background: etaLate ? colors.warningBg : colors.primaryLight,
                  borderRadius: 12, padding: 12, marginTop: 12,
                }}
              >
                <span style={{ fontSize: 20 }}>⏱️</span>
                <div>
                  <div className="t-title" style={{ color: etaLate ? colors.warning : colors.primary }}>
                    {etaText}
                  </div>
                  <div className="t-tiny">
                    {order.readyAt
                      ? `${(order.partner && order.partner.businessName) || 'The shop'} had it ready in ${order.prepTookMinutes} min`
                      : `${(order.partner && order.partner.businessName) || 'The shop'} is preparing your order`}
                  </div>
                </div>
              </div>
            ) : null}

            {isClosed ? (
              <div
                className="row"
                style={{
                  gap: 12, marginTop: 14, background: colors.dangerBg,
                  padding: 14, borderRadius: 12, alignItems: 'flex-start',
                }}
              >
                <span style={{ fontSize: 22 }}>❌</span>
                <div className="grow">
                  <div className="t-title">Order {order.status}</div>
                  {order.cancelReason ? <div className="t-small">{order.cancelReason}</div> : null}
                </div>
              </div>
            ) : (
              <StatusTimeline flow={FLOW} status={order.status} timeline={order.timeline} />
            )}
          </Reveal>

          {/* Rider - appears once the shop marks it ready and a rider is found. */}
          {order.delivery && order.delivery.status !== 'none' ? (
            <Reveal delay={40} className="card sh-sm" style={{ marginTop: 16 }}>
              <div className="row" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
                <h3 className="t-title">Delivery</h3>
                <Badge value={{
                  searching: 'Finding rider', assigned: 'Rider on the way', at_shop: 'At the shop',
                  picked_up: 'Out for delivery', delivered: 'Delivered', no_riders: 'No rider yet',
                }[order.delivery.status] || order.delivery.status} />
              </div>

              {order.delivery.status === 'searching' ? (
                <div className="t-small" style={{ marginTop: 10 }}>🛵 Ringing nearby riders for your order…</div>
              ) : null}

              {order.delivery.rider ? (
                <div className="row" style={{ gap: 12, marginTop: 12, alignItems: 'center' }}>
                  <div style={{
                    width: 44, height: 44, borderRadius: 22, background: colors.primaryLight,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20,
                  }}>🛵</div>
                  <div className="grow">
                    <div className="t-title">{order.delivery.rider.businessName}</div>
                    <div className="t-tiny">
                      {titleCase((order.delivery.rider.vehicleDetails && order.delivery.rider.vehicleDetails.type) || 'bike')}
                      {' · '}
                      {{
                        assigned: 'heading to the shop', at_shop: 'collecting your order',
                        picked_up: 'on the way to you', delivered: 'delivered',
                      }[order.delivery.status] || ''}
                    </div>
                  </div>
                  {order.delivery.rider.phone && order.delivery.status !== 'delivered' ? (
                    <a href={`tel:${order.delivery.rider.phone}`} className="btn btn-light btn-sm">📞 Call</a>
                  ) : null}
                </div>
              ) : null}

              {['assigned', 'at_shop', 'picked_up'].includes(order.delivery.status) && order.delivery.ride && order.delivery.ride.pickup ? (
                <LiveTripMap ride={order.delivery.ride} who="customer" height={240} style={{ marginTop: 12 }} />
              ) : null}
            </Reveal>
          ) : null}

          <Reveal delay={80} className="card sh-sm" style={{ marginTop: 16 }}>
            <h3 className="t-title" style={{ marginBottom: 12 }}>Items</h3>
            {order.items.map((item, i) => (
              // eslint-disable-next-line react/no-array-index-key
              <div key={i} className="row" style={{ gap: 12, padding: '9px 0' }}>
                {item.image ? (
                  <img
                    src={fileUrl(item.image)}
                    alt=""
                    style={{ width: 46, height: 46, borderRadius: 10, objectFit: 'cover', background: colors.bg }}
                  />
                ) : (
                  <span
                    style={{
                      width: 46, height: 46, borderRadius: 10, background: colors.primaryLight,
                      display: 'grid', placeItems: 'center', fontSize: 18, flex: '0 0 auto',
                    }}
                  >
                    📦
                  </span>
                )}
                <div className="grow">
                  <div className="t-body">{item.title}</div>
                  <div className="t-tiny">{item.unit} · Qty {item.quantity}</div>
                </div>
                <div className="t-title">{currency(item.total)}</div>
              </div>
            ))}
            <div className="divider" />
            <Row label="Item total" value={currency(order.subtotal)} />
            <Row label="Delivery" value={order.deliveryCharge ? currency(order.deliveryCharge) : 'Free'} />
            <ChargeRows doc={order} />
            <div className="divider" />
            <Row label="Total" value={currency(order.totalAmount)} valueStyle={{ fontSize: 17, fontWeight: 800 }} />
            <Row
              label="Payment"
              value={`${(order.paymentMode || '').toUpperCase()} · ${titleCase(order.paymentStatus)}`}
            />
          </Reveal>

          <Reveal delay={140} className="card sh-sm" style={{ marginTop: 16 }}>
            <h3 className="t-title" style={{ marginBottom: 8 }}>Delivery address</h3>
            <p className="t-body" style={{ lineHeight: 1.7, whiteSpace: 'pre-line' }}>
              {[
                order.address && order.address.name,
                [
                  order.address && order.address.line1,
                  order.address && order.address.line2,
                  order.address && order.address.landmark,
                ].filter(Boolean).join(', '),
                [order.address && order.address.city, order.address && order.address.pincode]
                  .filter(Boolean).join(' - '),
                order.address && order.address.phone ? `📞 ${order.address.phone}` : '',
              ].filter(Boolean).join('\n')}
            </p>
          </Reveal>
        </div>

        <div className="split-side col" style={{ gap: 12 }}>
          <Button
            title={`Call ${(order.partner && order.partner.businessName) || 'shop'}`}
            icon="📞"
            variant="ghost"
            full
            onClick={() => { window.location.href = `tel:${order.partner && order.partner.phone}`; }}
          />
          {order.status === 'delivered' ? (
            <Button
              title="Rate this order"
              icon="⭐"
              variant="accent"
              full
              onClick={() => navigate('/review', {
                state: {
                  targetType: 'partner',
                  target: order.partner && order.partner._id,
                  order: order._id,
                  title: order.partner && order.partner.businessName,
                },
              })}
            />
          ) : null}
          {order.awaitingPayment && !isClosed ? (
            <div style={{ background: 'var(--warning-bg)', borderRadius: 12, padding: 12 }}>
              <strong style={{ color: 'var(--warning)' }}>⏳ Waiting for payment</strong>
              <div className="t-small" style={{ marginTop: 3 }}>
                {(order.partner && order.partner.businessName) || 'The shop'} receives your order once it is paid.
              </div>
            </div>
          ) : null}
          {order.awaitingPayment && !isClosed ? (
            <Button title="Cash on delivery instead" icon="💵" variant="light" full
              onClick={() => api.orders.setPaymentMode(id, { paymentMode: 'cod' })
                .then(() => { toast.success('Pay cash when it arrives.', 'Order placed'); load(); })
                .catch((err) => toast.error(err.message, 'Could not switch'))} />
          ) : null}
          {order.paymentMode === 'online' && order.paymentStatus !== 'paid' && !isClosed ? (
            <Button
              title={`${order.awaitingPayment ? 'Retry payment' : 'Pay'} · ${currency(order.totalAmount)}`}
              icon="💳"
              variant="accent"
              loading={paying}
              full
              onClick={payNow}
            />
          ) : null}
          {canCancel ? <Button title="Cancel order" variant="danger" full onClick={cancel} /> : null}
        </div>
      </div>
    </div>
  );
};

export default OrderDetailPage;
